/**
 * UserManagement Page
 *
 * Super Admin page for managing users with CRUD operations,
 * filtering, sorting, and pagination.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  RefreshCw,
  Download,
  AlertCircle,
  X,
  ChevronLeft,
  FileText,
} from 'lucide-react';
import { LoadingState } from '../components/shared/LoadingState';
import { Card } from '../components/shared/Card';
import {
  UserTable,
  CreateUserModal,
  EditUserModal,
  ConfirmDialog,
  Pagination,
  ViewUserPermissionsModal,
  GrantPermissionModal,
  PermissionAuditLog,
} from '../components/admin';
import { superAdminApi } from '../services/adminApi';
import type {
  AdminUser,
  AdminCompany,
  CreateUserData,
  UpdateUserData,
  UserQueryParams,
  UserRole,
  SubscriptionTier,
  UserStatus,
  GrantPermissionData,
} from '../services/adminApi';

export const UserManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<'users' | 'audit'>('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | ''>('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<AdminUser | null>(null);
  const [currentUserPermissions, setCurrentUserPermissions] = useState<string[]>([]);

  // Check if we should open create modal from navigation state
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowCreateModal(true);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Fetch companies for dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await superAdminApi.getAllCompanies({ limit: 1000 });
        setCompanies(response.data);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
        // Mock data for demo
        setCompanies([
          { id: 1, name: 'Acme Corp', domain: 'acme.com', subscriptionTier: 'enterprise', usersCount: 45, maxUsers: 100, status: 'active', createdAt: '2024-01-15' },
          { id: 2, name: 'Tech Solutions', domain: 'techsol.com', subscriptionTier: 'pro', usersCount: 12, maxUsers: 50, status: 'active', createdAt: '2024-02-20' },
          { id: 3, name: 'Freight Inc', domain: 'freight.com', subscriptionTier: 'basic', usersCount: 5, maxUsers: 10, status: 'active', createdAt: '2024-03-10' },
        ]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch users with current filters
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params: UserQueryParams = {
      page: currentPage,
      limit: itemsPerPage,
      sortBy: sortBy as 'email' | 'createdAt' | 'lastLogin' | 'companyName',
      sortOrder,
    };

    if (searchQuery) params.search = searchQuery;
    if (roleFilter) params.role = roleFilter;
    if (tierFilter) params.subscriptionTier = tierFilter;
    if (statusFilter) params.status = statusFilter;

    try {
      const response = await superAdminApi.getAllUsers(params);
      setUsers(response.data);
      setTotalUsers(response.total);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users. Showing demo data.');
      // Mock data for demo
      const mockUsers: AdminUser[] = [
        { id: 1, email: 'admin@platform.com', firstName: 'Super', lastName: 'Admin', role: 'super_admin', subscriptionTier: 'enterprise', status: 'active', lastLogin: new Date().toISOString(), createdAt: '2024-01-01' },
        { id: 2, email: 'john@acme.com', firstName: 'John', lastName: 'Doe', role: 'company_admin', companyId: 1, companyName: 'Acme Corp', subscriptionTier: 'enterprise', status: 'active', lastLogin: new Date(Date.now() - 86400000).toISOString(), createdAt: '2024-01-15' },
        { id: 3, email: 'jane@techsol.com', firstName: 'Jane', lastName: 'Smith', role: 'user', companyId: 2, companyName: 'Tech Solutions', subscriptionTier: 'pro', status: 'active', lastLogin: new Date(Date.now() - 172800000).toISOString(), createdAt: '2024-02-20' },
        { id: 4, email: 'bob@freight.com', firstName: 'Bob', lastName: 'Wilson', role: 'user', companyId: 3, companyName: 'Freight Inc', subscriptionTier: 'basic', status: 'inactive', createdAt: '2024-03-10' },
        { id: 5, email: 'alice@acme.com', firstName: 'Alice', lastName: 'Johnson', role: 'user', companyId: 1, companyName: 'Acme Corp', subscriptionTier: 'enterprise', status: 'active', lastLogin: new Date(Date.now() - 3600000).toISOString(), createdAt: '2024-03-15' },
      ];
      setUsers(mockUsers);
      setTotalUsers(mockUsers.length);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchQuery, roleFilter, tierFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handlers
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('');
    setTierFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      await superAdminApi.createUser(data);
      fetchUsers();
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateUser = async (id: number, data: UpdateUserData) => {
    try {
      await superAdminApi.updateUser(id, data);
      fetchUsers();
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await superAdminApi.deleteUser(selectedUser.id);
      fetchUsers();
    } catch (err) {
      throw err;
    }
  };

  const handleEditClick = (user: AdminUser) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteClick = (user: AdminUser) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleViewDetails = (user: AdminUser) => {
    // Could navigate to user detail page or show modal
    console.log('View details for:', user);
    alert(`User Details:\n\nID: ${user.id}\nEmail: ${user.email}\nRole: ${user.role}\nCompany: ${user.companyName || 'N/A'}\nStatus: ${user.status}`);
  };

  const handleViewPermissions = async (user: AdminUser) => {
    setPermissionsUser(user);
    setShowPermissionsModal(true);

    // Fetch current permissions for the grant modal
    try {
      const permissionsData = await superAdminApi.getUserPermissions(user.id);
      const effectivePermissions = permissionsData.permissions.map(p => p.permission_key);
      setCurrentUserPermissions(effectivePermissions);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      setCurrentUserPermissions([]);
    }
  };

  const handleOpenGrantModal = (userId: number) => {
    setShowGrantModal(true);
  };

  const handleGrantPermission = async (data: GrantPermissionData) => {
    if (!permissionsUser) return;

    try {
      await superAdminApi.grantPermission(permissionsUser.id, data);
      // Refresh user list and permissions
      fetchUsers();

      // Refresh current permissions
      const permissionsData = await superAdminApi.getUserPermissions(permissionsUser.id);
      const effectivePermissions = permissionsData.permissions.map(p => p.permission_key);
      setCurrentUserPermissions(effectivePermissions);

      // Close grant modal
      setShowGrantModal(false);
    } catch (error) {
      throw error;
    }
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const hasActiveFilters = !!searchQuery || !!roleFilter || !!tierFilter || !!statusFilter;

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600" />
                User Management
              </h1>
              <p className="text-gray-600 mt-1">
                {totalUsers.toLocaleString()} total users
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchUsers}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <UserPlus className="w-4 h-4" />
                Add User
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Users
              </div>
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Audit Log
              </div>
            </button>
          </nav>
        </div>

        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>
            {/* Search and Filters */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by email or company..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </form>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {[searchQuery, roleFilter, tierFilter, statusFilter].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={roleFilter}
                    onChange={(e) => {
                      setRoleFilter(e.target.value as UserRole | '');
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Roles</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="company_admin">Company Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription</label>
                  <select
                    value={tierFilter}
                    onChange={(e) => {
                      setTierFilter(e.target.value as SubscriptionTier | '');
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Tiers</option>
                    <option value="free">Free</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as UserStatus | '');
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="mt-4 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </Card>

        {/* Users Table */}
        {loading ? (
          <LoadingState message="Loading users..." />
        ) : (
          <>
            <UserTable
              users={users}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onViewDetails={handleViewDetails}
              onViewPermissions={handleViewPermissions}
              emptyMessage={
                hasActiveFilters
                  ? 'No users match your filters'
                  : 'No users found'
              }
            />

            {/* Pagination */}
            {totalUsers > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalUsers}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        )}
        </>
        )}

        {/* Audit Log Tab Content */}
        {activeTab === 'audit' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <PermissionAuditLog showFilters={true} />
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateUser}
        companies={companies}
        loadingCompanies={loadingCompanies}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUpdateUser}
        user={selectedUser}
        companies={companies}
        loadingCompanies={loadingCompanies}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${selectedUser?.email}? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      <ViewUserPermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => {
          setShowPermissionsModal(false);
          setPermissionsUser(null);
          setCurrentUserPermissions([]);
        }}
        user={permissionsUser}
        onGrantPermission={handleOpenGrantModal}
        onRefresh={fetchUsers}
      />

      <GrantPermissionModal
        isOpen={showGrantModal}
        onClose={() => setShowGrantModal(false)}
        user={permissionsUser}
        currentPermissions={currentUserPermissions}
        onSubmit={handleGrantPermission}
      />
    </div>
  );
};

export default UserManagement;
